// packages import
import { IconButton, Menu, MenuItem } from '@mui/material'
import { FC, MouseEvent, useContext, useState } from 'react'
import MoreVertIcon from '@mui/icons-material/MoreVert'

// component import
import { Alert } from '../common/Alert'
import { ConfirmationModal } from '../common/ConfirmationModal'

// others
import { FetchJournalEntryQuery, useDeleteManualJournalEntryMutation } from '../../../generated'
import { AuthContext } from '../../context'
import { hasAdminAccess } from '../../utils'

interface JournalEntryMenuProps {
  journalEntry: FetchJournalEntryQuery['fetchJournalEntry']
  onRefetch: () => void
  onDuplicate: (id: string) => void
}

export const JournalEntryMenu: FC<JournalEntryMenuProps> = ({ journalEntry, onRefetch, onDuplicate }) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { currentUser } = useContext(AuthContext)

  const hasAdminPermissions = hasAdminAccess(currentUser)
  const { id, journalEntryNumber, isSystemGenerated } = journalEntry || {}
  const open = Boolean(anchorEl)

  const canDuplicate = hasAdminPermissions && !isSystemGenerated
  const canDelete = hasAdminPermissions && !isSystemGenerated
  const canView = canDuplicate || canDelete

  const [deleteJournalEntry, { loading: deletingJournalEntry }] = useDeleteManualJournalEntryMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted() {
      Alert.success('Journal entry deleted successfully!')
      onRefetch()
    }
  })

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleDuplicateClick = () => {
    if (id) onDuplicate(id)
    handleClose()
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true)
  }

  const handleCloseDeleteConfirmationModal = () => {
    setShowDeleteConfirmation(false)
    handleClose()
  }

  const handleConfirmDelete = () => {
    deleteJournalEntry({ variables: { id } })
  }

  if (!journalEntry || !canView) return null

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {canDuplicate && <MenuItem onClick={handleDuplicateClick}>Duplicate</MenuItem>}

        {canDelete && <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>}
      </Menu>

      {showDeleteConfirmation && (
        <ConfirmationModal
          isOpen={showDeleteConfirmation}
          title="Delete Journal Entry"
          message={`Are you sure you want to delete journal entry #${journalEntryNumber}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          isLoading={deletingJournalEntry}
          onConfirm={handleConfirmDelete}
          onCancel={handleCloseDeleteConfirmationModal}
        />
      )}
    </>
  )
}
